/**
 * Auto Scene Split Hook
 * Feature: Scene Editor Redesign
 *
 * Automatically triggers scene splitting when the page loads
 * and no scenes exist. Always uses Claude Sonnet (no model selection UI).
 */

import { useEffect, useRef } from 'react';
import type { Id } from '../../convex/_generated/dataModel';
import type { ModelKey } from '../@types/reels';
import type { SceneSplitStatus } from '../@types/reelScenes';

interface IAutoSceneSplitOptions {
  reelId: Id<'reels'>;
  hasScript: boolean;
  scenesCount: number;
  sceneSplitStatus: SceneSplitStatus | undefined;
  isSplitting: boolean;
  splitReel: (modelKey: ModelKey) => Promise<void>;
  /** Called after splitting completes and scenes are created */
  onSplitComplete?: () => void;
}

interface IAutoSceneSplitResult {
  isAutoSplitting: boolean;
  autoSplitTriggered: boolean;
  noScriptError: boolean;
}

export function useAutoSceneSplit({
  reelId,
  hasScript,
  scenesCount,
  sceneSplitStatus,
  isSplitting,
  splitReel,
  onSplitComplete,
}: IAutoSceneSplitOptions): IAutoSceneSplitResult {
  // Track whether auto-split has been triggered for the current reel.
  // Only accessed inside effects, never during render.
  const hasTriggeredRef = useRef(false);
  const currentReelIdRef = useRef(reelId);
  // Track previous scenes count to detect when scenes first appear after a split
  const prevScenesCountRef = useRef(0);

  const noScriptError = scenesCount === 0 && !hasScript;

  // Derive auto-splitting status from external state:
  // If splitting is active and was triggered in the current cycle
  // (sceneSplitStatus transitions from undefined/not_split to splitting),
  // the auto-split is in progress.
  const isAutoSplitting =
    isSplitting ||
    sceneSplitStatus === 'splitting';

  // The split was already triggered if scenes exist or status is 'split'
  const autoSplitTriggered =
    sceneSplitStatus === 'split' ||
    sceneSplitStatus === 'splitting' ||
    scenesCount > 0;

  useEffect(() => {
    // Reset trigger tracking when navigating to a different reel
    if (currentReelIdRef.current !== reelId) {
      hasTriggeredRef.current = false;
      currentReelIdRef.current = reelId;
    }

    // Guard: only auto-split when conditions are met
    if (hasTriggeredRef.current) return;
    if (scenesCount > 0) return;
    if (!hasScript) return;
    if (sceneSplitStatus === 'splitting' || sceneSplitStatus === 'split') return;
    if (isSplitting) return;

    // Mark as triggered and invoke the external split action
    hasTriggeredRef.current = true;
    splitReel('claude');
  }, [reelId, scenesCount, hasScript, sceneSplitStatus, isSplitting, splitReel]);

  // Fire onSplitComplete when scenes first appear after an auto-triggered split
  useEffect(() => {
    if (
      prevScenesCountRef.current === 0 &&
      scenesCount > 0 &&
      hasTriggeredRef.current &&
      onSplitComplete
    ) {
      onSplitComplete();
    }
    prevScenesCountRef.current = scenesCount;
  }, [scenesCount, onSplitComplete]);

  return {
    isAutoSplitting,
    autoSplitTriggered,
    noScriptError,
  };
}
